'use client';

import Image from '@/components/Image';
import { env } from '@/utils/env';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { StyleHotProductRawCategoryTab } from '../types';
import './StyleCategorySwiper.css';

interface StyleCategorySwiperProps {
  tabs?: StyleHotProductRawCategoryTab[];
  activeTabIndex?: number;
  onActiveTabChange?: (tabIndex: number) => void;
  onUserNavigate?: (swiperEl: HTMLElement) => void;
}

interface StyleCategorySlide {
  key: string;
  title: string;
  imageSrc: string;
  tabIndex: number;
}

type SwiperSlideElement = HTMLElement & {
  progress?: number;
};

type SwiperWithAllowClick = SwiperType & {
  allowClick?: boolean;
};

interface StyleCategorySlot {
  progress: number;
  x: number;
  y: number;
  width: number;
  height: number;
  opacity: number;
  path: StyleCategoryPathCommand[];
}

interface StyleCategoryPathCommand {
  command: 'M' | 'L' | 'C' | 'Z';
  points: Array<[number, number]>;
}

const REPEAT_COUNT = 7;
const MIDDLE_REPEAT_INDEX = Math.floor(REPEAT_COUNT / 2);
const DEFAULT_ACTIVE_OFFSET = 2;
const STYLE_CATEGORY_SWIPER_SPEED = 0;
const OUTSIDE_SLOT_VISIBILITY_OFFSET = 0.35;
const DESIGN_CENTER_CARD_RADIUS = 20;
const SIDE_CARD_IMAGE_ROTATE_Y = 28;
const DESIGN_CARD_ASPECT_RATIO = 450 / 600;

const CENTER_SLOT_LEFT_PATH: StyleCategoryPathCommand[] = [
  { command: 'M', points: [[1, DESIGN_CENTER_CARD_RADIUS / 600]] },
  {
    command: 'C',
    points: [
      [1, 8.954 / 600],
      [441.046 / 450, 0],
      [1 - DESIGN_CENTER_CARD_RADIUS / 450, 0],
    ],
  },
  { command: 'L', points: [[DESIGN_CENTER_CARD_RADIUS / 450, 0]] },
  {
    command: 'C',
    points: [
      [8.954 / 450, 0],
      [0, 8.954 / 600],
      [0, DESIGN_CENTER_CARD_RADIUS / 600],
    ],
  },
  { command: 'L', points: [[0, 1 - DESIGN_CENTER_CARD_RADIUS / 600]] },
  {
    command: 'C',
    points: [
      [0, 591.046 / 600],
      [8.954 / 450, 1],
      [DESIGN_CENTER_CARD_RADIUS / 450, 1],
    ],
  },
  { command: 'L', points: [[1 - DESIGN_CENTER_CARD_RADIUS / 450, 1]] },
  {
    command: 'C',
    points: [
      [441.046 / 450, 1],
      [1, 591.046 / 600],
      [1, 1 - DESIGN_CENTER_CARD_RADIUS / 600],
    ],
  },
  { command: 'L', points: [[1, DESIGN_CENTER_CARD_RADIUS / 600]] },
  { command: 'Z', points: [] },
];

const CENTER_SLOT_RIGHT_PATH: StyleCategoryPathCommand[] = [
  { command: 'M', points: [[0, DESIGN_CENTER_CARD_RADIUS / 600]] },
  {
    command: 'C',
    points: [
      [0, 8.954 / 600],
      [8.954 / 450, 0],
      [DESIGN_CENTER_CARD_RADIUS / 450, 0],
    ],
  },
  { command: 'L', points: [[1 - DESIGN_CENTER_CARD_RADIUS / 450, 0]] },
  {
    command: 'C',
    points: [
      [441.046 / 450, 0],
      [1, 8.954 / 600],
      [1, DESIGN_CENTER_CARD_RADIUS / 600],
    ],
  },
  { command: 'L', points: [[1, 1 - DESIGN_CENTER_CARD_RADIUS / 600]] },
  {
    command: 'C',
    points: [
      [1, 591.046 / 600],
      [441.046 / 450, 1],
      [1 - DESIGN_CENTER_CARD_RADIUS / 450, 1],
    ],
  },
  { command: 'L', points: [[DESIGN_CENTER_CARD_RADIUS / 450, 1]] },
  {
    command: 'C',
    points: [
      [8.954 / 450, 1],
      [0, 591.046 / 600],
      [0, 1 - DESIGN_CENTER_CARD_RADIUS / 600],
    ],
  },
  { command: 'L', points: [[0, DESIGN_CENTER_CARD_RADIUS / 600]] },
  { command: 'Z', points: [] },
];

// Coordinates are normalized from the Figma SVG mask0-mask3 paths in a 1824x764 stage.
const STYLE_CATEGORY_SLOTS: StyleCategorySlot[] = [
  {
    progress: -2,
    x: 0,
    y: 125,
    width: 299,
    height: 514,
    opacity: 0.4,
    path: [
      { command: 'M', points: [[295.722 / 299, 47.406 / 514]] },
      {
        command: 'C',
        points: [
          [295.654 / 299, 38.8 / 514],
          [288.283 / 299, 31.545 / 514],
          [278.423 / 299, 30.38 / 514],
        ],
      },
      { command: 'L', points: [[22.7167 / 299, 0.162 / 514]] },
      {
        command: 'C',
        points: [
          [10.7017 / 299, -1.258 / 514],
          [0, 6.819 / 514],
          [0, 17.306 / 514],
        ],
      },
      { command: 'L', points: [[0, 496.694 / 514]] },
      {
        command: 'C',
        points: [
          [0, 507.2 / 514],
          [10.7381 / 299, 515.282 / 514],
          [22.7702 / 299, 513.831 / 514],
        ],
      },
      { command: 'L', points: [[281.754 / 299, 482.611 / 514]] },
      {
        command: 'C',
        points: [
          [291.693 / 299, 481.412 / 514],
          [299.068 / 299, 474.033 / 514],
          [1, 465.356 / 514],
        ],
      },
      { command: 'L', points: [[295.722 / 299, 47.406 / 514]] },
      { command: 'Z', points: [] },
    ],
  },
  {
    progress: -1,
    x: 323,
    y: 86,
    width: 340,
    height: 592,
    opacity: 0.6,
    path: [
      { command: 'M', points: [[336.237 / 340, 52.611 / 592]] },
      {
        command: 'C',
        points: [
          [336.17 / 340, 44.012 / 592],
          [328.816 / 340, 36.759 / 592],
          [318.969 / 340, 35.581 / 592],
        ],
      },
      { command: 'L', points: [[22.746 / 340, 0.166 / 592]] },
      {
        command: 'C',
        points: [
          [10.722 / 340, -1.2715 / 592],
          [0, 6.8106 / 592],
          [0, 17.312 / 592],
        ],
      },
      { command: 'L', points: [[0, 574.688 / 592]] },
      {
        command: 'C',
        points: [
          [0, 585.208 / 592],
          [10.758 / 340, 593.296 / 592],
          [22.8 / 340, 591.827 / 592],
        ],
      },
      { command: 'L', points: [[322.786 / 340, 555.24 / 592]] },
      {
        command: 'C',
        points: [
          [332.709 / 340, 554.03 / 592],
          [340.067 / 340, 546.655 / 592],
          [1, 537.985 / 592],
        ],
      },
      { command: 'L', points: [[336.237 / 340, 52.611 / 592]] },
      { command: 'Z', points: [] },
    ],
  },
  {
    progress: 0,
    x: 687,
    y: 82,
    width: 450,
    height: 600,
    opacity: 1,
    path: CENTER_SLOT_LEFT_PATH,
  },
  {
    progress: 1,
    x: 1161,
    y: 86,
    width: 340,
    height: 592,
    opacity: 0.6,
    path: [
      { command: 'M', points: [[3.76 / 340, 52.611 / 592]] },
      {
        command: 'C',
        points: [
          [3.83 / 340, 44.012 / 592],
          [11.18 / 340, 36.759 / 592],
          [21.03 / 340, 35.581 / 592],
        ],
      },
      { command: 'L', points: [[317.25 / 340, 0.166 / 592]] },
      {
        command: 'C',
        points: [
          [329.28 / 340, -1.2715 / 592],
          [1, 6.8106 / 592],
          [1, 17.312 / 592],
        ],
      },
      { command: 'L', points: [[1, 574.688 / 592]] },
      {
        command: 'C',
        points: [
          [1, 585.208 / 592],
          [329.24 / 340, 593.296 / 592],
          [317.2 / 340, 591.827 / 592],
        ],
      },
      { command: 'L', points: [[17.21 / 340, 555.24 / 592]] },
      {
        command: 'C',
        points: [
          [7.29 / 340, 554.03 / 592],
          [-0.067 / 340, 546.655 / 592],
          [0, 537.985 / 592],
        ],
      },
      { command: 'L', points: [[3.76 / 340, 52.611 / 592]] },
      { command: 'Z', points: [] },
    ],
  },
  {
    progress: 2,
    x: 1525,
    y: 125,
    width: 299,
    height: 514,
    opacity: 0.4,
    path: [
      { command: 'M', points: [[3.28 / 299, 47.406 / 514]] },
      {
        command: 'C',
        points: [
          [3.35 / 299, 38.8 / 514],
          [10.72 / 299, 31.545 / 514],
          [20.58 / 299, 30.38 / 514],
        ],
      },
      { command: 'L', points: [[276.28 / 299, 0.162 / 514]] },
      {
        command: 'C',
        points: [
          [288.3 / 299, -1.258 / 514],
          [1, 6.819 / 514],
          [1, 17.306 / 514],
        ],
      },
      { command: 'L', points: [[1, 496.694 / 514]] },
      {
        command: 'C',
        points: [
          [1, 507.2 / 514],
          [288.26 / 299, 515.282 / 514],
          [276.23 / 299, 513.831 / 514],
        ],
      },
      { command: 'L', points: [[17.25 / 299, 482.61 / 514]] },
      {
        command: 'C',
        points: [
          [7.31 / 299, 481.412 / 514],
          [-0.07 / 299, 474.033 / 514],
          [0, 465.355 / 514],
        ],
      },
      { command: 'L', points: [[3.28 / 299, 47.406 / 514]] },
      { command: 'Z', points: [] },
    ],
  },
];

function lerp(from: number, to: number, progress: number): number {
  return from + (to - from) * progress;
}

function getSlotByProgress(progress: number): StyleCategorySlot {
  if (progress <= STYLE_CATEGORY_SLOTS[0].progress) {
    return STYLE_CATEGORY_SLOTS[0];
  }

  const lastSlot = STYLE_CATEGORY_SLOTS[STYLE_CATEGORY_SLOTS.length - 1];

  if (progress >= lastSlot.progress) {
    return lastSlot;
  }

  const previousSlotIndex = STYLE_CATEGORY_SLOTS.findIndex(
    (slot, index) =>
      index < STYLE_CATEGORY_SLOTS.length - 1 &&
      progress >= slot.progress &&
      progress <= STYLE_CATEGORY_SLOTS[index + 1].progress,
  );
  const fromSlot = STYLE_CATEGORY_SLOTS[previousSlotIndex];
  const toSlot = STYLE_CATEGORY_SLOTS[previousSlotIndex + 1];
  const slotProgress = (progress - fromSlot.progress) / (toSlot.progress - fromSlot.progress);
  const fromPath =
    fromSlot.progress === 0 && toSlot.progress > 0 ? CENTER_SLOT_RIGHT_PATH : fromSlot.path;
  const toPath =
    toSlot.progress === 0 && fromSlot.progress > 0 ? CENTER_SLOT_RIGHT_PATH : toSlot.path;

  return {
    progress,
    x: lerp(fromSlot.x, toSlot.x, slotProgress),
    y: lerp(fromSlot.y, toSlot.y, slotProgress),
    width: lerp(fromSlot.width, toSlot.width, slotProgress),
    height: lerp(fromSlot.height, toSlot.height, slotProgress),
    opacity: lerp(fromSlot.opacity, toSlot.opacity, slotProgress),
    path: fromPath.map((command, commandIndex) => ({
      command: command.command,
      points: command.points.map(([x, y], pointIndex) => {
        const [toX, toY] = toPath[commandIndex].points[pointIndex];

        return [lerp(x, toX, slotProgress), lerp(y, toY, slotProgress)];
      }),
    })),
  };
}

function toClipPath(slot: StyleCategorySlot): string {
  const path = slot.path
    .map(({ command, points }) => {
      if (command === 'Z') {
        return command;
      }

      const values = points
        .map(([x, y]) => `${(x * slot.width).toFixed(3)} ${(y * slot.height).toFixed(3)}`)
        .join(' ');

      return `${command}${values}`;
    })
    .join(' ');

  return `path("${path}")`;
}

function getStageEl(swiper: SwiperType): HTMLElement | null {
  return (
    swiper.el
      .closest('.style-hot-product-category-shell')
      ?.querySelector<HTMLElement>('.style-hot-product-category-stage') || null
  );
}

function isActiveSlide(swiper: SwiperType, slideEl: Element): boolean {
  const slideIndex = Number(slideEl.getAttribute('data-style-slide-index'));

  return slideIndex === swiper.activeIndex || slideEl.classList.contains('swiper-slide-active');
}

function updateCarouselStyles(swiper: SwiperType) {
  const stageEl = getStageEl(swiper);

  if (!stageEl) {
    return;
  }

  swiper.slides.forEach((slideEl) => {
    const card = stageEl.querySelector<HTMLElement>(
      `.style-hot-product-category-card[data-style-slide-index="${slideEl.getAttribute(
        'data-style-slide-index',
      )}"]`,
    );

    if (!card) {
      return;
    }

    const slideProgress = -((slideEl as SwiperSlideElement).progress || 0);
    const isOutside =
      slideProgress < STYLE_CATEGORY_SLOTS[0].progress - OUTSIDE_SLOT_VISIBILITY_OFFSET ||
      slideProgress >
        STYLE_CATEGORY_SLOTS[STYLE_CATEGORY_SLOTS.length - 1].progress +
          OUTSIDE_SLOT_VISIBILITY_OFFSET;
    const progress = Math.max(Math.min(slideProgress, 2), -2);
    const absProgress = Math.abs(progress);
    const imagePerspectiveProgress = Math.min(absProgress, 1);
    const imageRotateY = -Math.sign(progress) * SIDE_CARD_IMAGE_ROTATE_Y;
    const slot = getSlotByProgress(progress);
    const imageWidth = slot.height * DESIGN_CARD_ASPECT_RATIO;

    card.style.setProperty('--style-card-x', `${slot.x.toFixed(3)}px`);
    card.style.setProperty('--style-card-y', `${slot.y.toFixed(3)}px`);
    card.style.setProperty('--style-card-width', `${slot.width.toFixed(3)}px`);
    card.style.setProperty('--style-card-height', `${slot.height.toFixed(3)}px`);
    card.style.setProperty('--style-card-clip-path', toClipPath(slot));
    card.style.setProperty(
      '--style-card-image-rotate-y',
      `${(imageRotateY * imagePerspectiveProgress).toFixed(3)}deg`,
    );
    card.style.setProperty('--style-card-image-width', `${imageWidth.toFixed(3)}px`);
    card.style.opacity = isOutside ? '0' : String(slot.opacity);
    card.style.zIndex = String(100 - Math.round(absProgress * 10));

    if (isActiveSlide(swiper, slideEl)) {
      card.style.setProperty('--style-card-transition-duration', '0ms');
    }
  });
}

function updateCarouselTransition(swiper: SwiperType, duration = 0) {
  const stageEl = getStageEl(swiper);

  if (!stageEl) {
    return;
  }

  swiper.slides.forEach((slideEl) => {
    const card = stageEl.querySelector<HTMLElement>(
      `.style-hot-product-category-card[data-style-slide-index="${slideEl.getAttribute(
        'data-style-slide-index',
      )}"]`,
    );

    if (!card) {
      return;
    }

    card.style.setProperty(
      '--style-card-transition-duration',
      isActiveSlide(swiper, slideEl) ? '0ms' : `${duration}ms`,
    );
  });
}

function resolveAssetUrl(path?: string | null): string {
  if (!path) {
    return '';
  }

  if (/^(https?:)?\/\//.test(path) || path.startsWith('blob:')) {
    return path;
  }

  if (path.startsWith('/admin/')) {
    return `${env.NEXT_PUBLIC_S3_URL}${path}`;
  }

  return path;
}

function normalizeSlides(tabs?: StyleHotProductRawCategoryTab[]): StyleCategorySlide[] {
  return (tabs || []).flatMap((tab, index) => {
    const imageSrc = resolveAssetUrl(tab.pc_tab_image || tab.wap_tab_image);

    if (!imageSrc) {
      return [];
    }

    const title = tab.tab_title || '';

    return [
      {
        key: String(tab.uid || tab.id || `${title}-${index}`),
        title,
        imageSrc,
        tabIndex: index,
      },
    ];
  });
}

function StyleCategoryCard({
  slide,
  index,
  slideIndex,
}: {
  slide: StyleCategorySlide;
  index: number;
  slideIndex: number;
}) {
  return (
    <div className="style-hot-product-category-card" data-style-slide-index={slideIndex}>
      <div className="style-hot-product-category-card-image">
        <Image
          src={slide.imageSrc}
          alt={slide.title || `style category ${index + 1}`}
          fill
          sizes="450px"
          className="block h-full w-full object-cover object-center"
        />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[rgba(0,0,0,0)] from-[68%] to-[rgba(0,0,0,0.42)]" />
    </div>
  );
}

export default function StyleCategorySwiper({
  tabs,
  activeTabIndex,
  onActiveTabChange,
  onUserNavigate,
}: StyleCategorySwiperProps) {
  const swiperRef = useRef<SwiperType | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const slides = useMemo(() => normalizeSlides(tabs), [tabs]);
  const swiperSlides = slides;
  const loopSlides = useMemo(
    () =>
      Array.from({ length: REPEAT_COUNT }, (_, repeatIndex) =>
        swiperSlides.map((slide) => ({
          ...slide,
          renderKey: `${repeatIndex}-${slide.key}`,
        })),
      ).flat(),
    [swiperSlides],
  );
  const activeSlideOffset = swiperSlides.findIndex((slide) => slide.tabIndex === activeTabIndex);
  const initialActiveOffset =
    activeSlideOffset >= 0
      ? activeSlideOffset
      : swiperSlides.length > DEFAULT_ACTIVE_OFFSET
        ? DEFAULT_ACTIVE_OFFSET
        : 0;
  const initialSlide = swiperSlides.length
    ? MIDDLE_REPEAT_INDEX * swiperSlides.length + initialActiveOffset
    : 0;

  const resetToMiddleRepeat = useCallback(
    (swiper: SwiperType) => {
      if (swiperSlides.length <= 1) {
        return;
      }

      const activeIndex = swiper.activeIndex;
      const realOffset = activeIndex % swiperSlides.length;
      const middleIndex = MIDDLE_REPEAT_INDEX * swiperSlides.length + realOffset;
      const lowerBoundary = swiperSlides.length * 2;
      const upperBoundary = swiperSlides.length * (REPEAT_COUNT - 2);

      if (activeIndex < lowerBoundary || activeIndex >= upperBoundary) {
        swiper.slideTo(middleIndex, 0, false);
      }
    },
    [swiperSlides.length],
  );

  const handleSwiper = useCallback(
    (swiper: SwiperType) => {
      swiperRef.current = swiper;
      requestAnimationFrame(() => {
        updateCarouselTransition(swiper, 0);
        updateCarouselStyles(swiper);
        const realOffset = swiperSlides.length ? swiper.activeIndex % swiperSlides.length : 0;
        onActiveTabChange?.(swiperSlides[realOffset]?.tabIndex || 0);
      });
    },
    [onActiveTabChange, swiperSlides],
  );

  const handleActiveSlideChange = useCallback(
    (swiper: SwiperType) => {
      if (swiperSlides.length === 0) {
        return;
      }

      const realOffset = swiper.activeIndex % swiperSlides.length;
      onActiveTabChange?.(swiperSlides[realOffset]?.tabIndex || 0);
    },
    [onActiveTabChange, swiperSlides],
  );

  const notifyUserNavigate = useCallback(() => {
    const shellEl = shellRef.current;

    if (!shellEl) {
      return;
    }

    onUserNavigate?.(shellEl);
  }, [onUserNavigate]);

  const handlePrev = useCallback(() => {
    const swiper = swiperRef.current;

    if (!swiper || swiper.destroyed) {
      return;
    }

    swiper.slidePrev(STYLE_CATEGORY_SWIPER_SPEED);
    notifyUserNavigate();
  }, [notifyUserNavigate]);

  const handleNext = useCallback(() => {
    const swiper = swiperRef.current;

    if (!swiper || swiper.destroyed) {
      return;
    }

    swiper.slideNext(STYLE_CATEGORY_SWIPER_SPEED);
    notifyUserNavigate();
  }, [notifyUserNavigate]);

  const handleCategoryClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const swiper = swiperRef.current;
      const allowClick = (swiper as SwiperWithAllowClick | null)?.allowClick;

      if (!swiper || swiper.destroyed || allowClick === false) {
        return;
      }

      if ((event.target as HTMLElement).closest('.style-hot-product-category-nav-button')) {
        return;
      }

      const cards = Array.from(
        event.currentTarget.querySelectorAll<HTMLElement>('.style-hot-product-category-card'),
      );
      const hitCard = cards
        .filter((card) => Number(card.style.opacity || '1') > 0)
        .sort((firstCard, secondCard) => {
          const firstZIndex = Number(firstCard.style.zIndex || '0');
          const secondZIndex = Number(secondCard.style.zIndex || '0');

          return secondZIndex - firstZIndex;
        })
        .find((card) => {
          const rect = card.getBoundingClientRect();

          return (
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom
          );
        });

      if (!hitCard) {
        return;
      }

      const slideIndex = Number(hitCard.dataset.styleSlideIndex);

      if (!Number.isFinite(slideIndex) || slideIndex === swiper.activeIndex) {
        return;
      }

      swiper.slideTo(slideIndex, STYLE_CATEGORY_SWIPER_SPEED);
      notifyUserNavigate();
    },
    [notifyUserNavigate],
  );

  useEffect(() => {
    const swiper = swiperRef.current;

    if (!swiper || !swiperSlides.length || activeTabIndex === undefined) {
      return;
    }

    const targetOffset = swiperSlides.findIndex((slide) => slide.tabIndex === activeTabIndex);

    if (targetOffset < 0) {
      return;
    }

    const currentOffset = swiper.activeIndex % swiperSlides.length;

    if (currentOffset === targetOffset) {
      return;
    }

    const repeatIndex = Math.floor(swiper.activeIndex / swiperSlides.length);
    swiper.slideTo(repeatIndex * swiperSlides.length + targetOffset, STYLE_CATEGORY_SWIPER_SPEED);
  }, [activeTabIndex, swiperSlides]);

  if (slides.length === 0) {
    return null;
  }

  return (
    <div
      ref={shellRef}
      className="style-hot-product-category-shell relative w-full overflow-hidden"
      onClick={handleCategoryClick}
    >
      <div className="style-hot-product-category-stage" aria-hidden="true">
        {loopSlides.map((slide, index) => (
          <StyleCategoryCard
            key={`stage-${slide.renderKey}`}
            slide={slide}
            index={index}
            slideIndex={index}
          />
        ))}
      </div>
      <Swiper
        key={`${slides.length}-${initialSlide}`}
        className="style-hot-product-category-swiper"
        slidesPerView="auto"
        centeredSlides
        initialSlide={initialSlide}
        spaceBetween={24}
        speed={STYLE_CATEGORY_SWIPER_SPEED}
        allowTouchMove={false}
        simulateTouch={false}
        watchSlidesProgress
        onSwiper={handleSwiper}
        onProgress={updateCarouselStyles}
        onSetTranslate={updateCarouselStyles}
        onSetTransition={updateCarouselTransition}
        onTouchStart={(swiper) => {
          updateCarouselTransition(swiper, 0);
        }}
        onSliderMove={updateCarouselStyles}
        onSlideChange={handleActiveSlideChange}
        onSlideChangeTransitionEnd={resetToMiddleRepeat}
      >
        {loopSlides.map((slide, index) => (
          <SwiperSlide key={slide.renderKey} aria-hidden="true" data-style-slide-index={index}>
            <span className="sr-only">{slide.title}</span>
          </SwiperSlide>
        ))}
      </Swiper>

      {slides.length > 1 && (
        <div className="style-hot-product-category-nav pointer-events-none absolute z-10">
          <button
            type="button"
            aria-label="Previous style"
            onClick={handlePrev}
            className="style-hot-product-category-nav-button style-hot-product-category-nav-button-prev pointer-events-auto flex cursor-pointer items-center justify-center border-0 bg-[rgba(0,0,0,0.5)] transition-colors hover:bg-[rgba(0,0,0,0.62)]"
          >
            <span
              aria-hidden="true"
              className="style-hot-product-category-nav-icon bg-[url('/zeelool-next/activity/style-hot-product/jump_left.svg')] bg-contain bg-center bg-no-repeat"
            />
          </button>
          <button
            type="button"
            aria-label="Next style"
            onClick={handleNext}
            className="style-hot-product-category-nav-button style-hot-product-category-nav-button-next pointer-events-auto flex cursor-pointer items-center justify-center border-0 bg-[rgba(0,0,0,0.5)] transition-colors hover:bg-[rgba(0,0,0,0.62)]"
          >
            <span
              aria-hidden="true"
              className="style-hot-product-category-nav-icon bg-[url('/zeelool-next/activity/style-hot-product/jump_right.svg')] bg-contain bg-center bg-no-repeat"
            />
          </button>
        </div>
      )}
    </div>
  );
}
